
import { useState, useEffect, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { useAuth } from '../contexts/AuthContext'
import { useChat } from '../contexts/ChatContext'
import { chatService } from '../services/api'
import './ChatWindow.css'

function ChatWindow({ outroUsuario, onClose }) {
    const { socket } = useSocket()
    const { usuario } = useAuth()
    const { mensagemInicial, setMensagemInicial } = useChat()
    const [mensagens, setMensagens] = useState([])
    const [novaMensagem, setNovaMensagem] = useState(mensagemInicial || '')
    const [loading, setLoading] = useState(true)
    const [imageError, setImageError] = useState(false)
    const [dadosUsuario, setDadosUsuario] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [loadingDados, setLoadingDados] = useState(false)
    const messagesEndRef = useRef(null)

    const abrirDadosUsuario = async () => {
        setShowModal(true)
        setLoadingDados(true)
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'https://api.agroov.com';
            const resp = await fetch(`${apiUrl.replace(/\/$/, '')}/api/usuarios/perfil/${outroUsuario.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await resp.json();
            if (data.usuario) {
                setDadosUsuario(data.usuario);
            }
        } catch (e) {
            console.error("Erro ao buscar dados do usuário:", e);
        } finally {
            setLoadingDados(false)
        }
    }

    // Carregar histórico inicial
    useEffect(() => {
        carregarMensagens();
        if (mensagemInicial) {
            setNovaMensagem(mensagemInicial);
            setMensagemInicial(''); // Limpa o contexto após consumir
        }
    }, [outroUsuario.id, mensagemInicial])

    // Scroll para o fim ao receber mensagem
    useEffect(() => {
        scrollToBottom()
    }, [mensagens])

    // Ouvir novas mensagens em tempo real
    useEffect(() => {
        if (!socket) return

        const handleNovaMensagem = (msg) => {
            // Só adiciona se for desta conversa (remetente ou destinatário é o outro usuário)
            if (msg.remetente_id === outroUsuario.id || msg.destinatario_id === outroUsuario.id) {
                setMensagens(prev => [...prev, msg])
                if (msg.remetente_id === outroUsuario.id) {
                    // Se foi o outro usuário quem enviou, o backend marca como lida ou nós limpamos o contador do Header
                    window.dispatchEvent(new CustomEvent('mensagens_lidas'));
                }
            }
        }

        socket.on('nova_mensagem', handleNovaMensagem)
        socket.on('mensagem_enviada', handleNovaMensagem)

        return () => {
            socket.off('nova_mensagem', handleNovaMensagem)
            socket.off('mensagem_enviada', handleNovaMensagem)
        }
    }, [socket, outroUsuario.id])

    const carregarMensagens = async () => {
        setLoading(true)
        try {
            const response = await chatService.listarMensagens(outroUsuario.id)
            setMensagens(response.mensagens)
            window.dispatchEvent(new CustomEvent('mensagens_lidas'));
        } catch (error) {
            console.error('Erro ao carregar mensagens:', error)
        } finally {
            setLoading(false)
        }
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    const handleEnviar = async (e) => {
        e.preventDefault()
        if (!novaMensagem.trim()) return

        try {
            // Envia via API (que salva no banco e emite o socket)
            const response = await chatService.enviarMensagem({
                destinatarioId: outroUsuario.id,
                conteudo: novaMensagem
            })

            // O socket vai cuidar de atualizar a lista através do evento 'mensagem_enviada'
            // setMensagens(prev => [...prev, response.mensagem])

            setNovaMensagem('')
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error)
            alert('Falha ao enviar mensagem')
        }
    }

    const getFotoUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.agroov.com';
        return `${apiUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
    }

    return (
        <div className="chat-window">
            <div className="chat-header">
                <div className="chat-user-info" onClick={abrirDadosUsuario} style={{ cursor: 'pointer' }} title="Clique para ver os dados de contato">
                    {outroUsuario.foto && !imageError ? (
                        <img 
                            src={getFotoUrl(outroUsuario.foto)} 
                            alt={outroUsuario.nome} 
                            className="chat-avatar" 
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="chat-avatar-placeholder">{outroUsuario.nome?.charAt(0)}</div>
                    )}
                    <h3>{outroUsuario.nome}</h3>
                </div>
                <button className="close-btn" onClick={onClose}>×</button>
            </div>

            <div className="chat-messages">
                {loading ? (
                    <div className="loading-msg">Carregando...</div>
                ) : mensagens.length === 0 ? (
                    <div className="empty-msg">Nenhuma mensagem ainda. Inicie a conversa!</div>
                ) : (
                    mensagens.map((msg, index) => {
                        const isMe = msg.remetente_id === usuario.id
                        return (
                            <div key={index} className={`message ${isMe ? 'me' : 'other'}`}>
                                <div className="message-content">
                                    <p>{msg.conteudo}</p>
                                    <span className="message-time">
                                        {new Date(msg.criado_em).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        )
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            <form className="chat-input-area" onSubmit={handleEnviar}>
                <input
                    type="text"
                    placeholder="Digite sua mensagem..."
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                />
                <button type="submit" disabled={!novaMensagem.trim()}>
                    ➤
                </button>
            </form>

            {showModal && (
                <div className="dados-usuario-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="dados-usuario-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h4>Dados de Contato</h4>
                            <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        {loadingDados ? (
                            <div className="modal-loading">Carregando dados...</div>
                        ) : dadosUsuario ? (
                            <div className="modal-body">
                                <div className="modal-avatar-section">
                                    {dadosUsuario.fotoPerfil ? (
                                        <img src={getFotoUrl(dadosUsuario.fotoPerfil)} alt={dadosUsuario.nome} className="modal-avatar" />
                                    ) : (
                                        <div className="modal-avatar-placeholder">{dadosUsuario.nome?.charAt(0).toUpperCase()}</div>
                                    )}
                                    <h4>{dadosUsuario.nome}</h4>
                                    <span className="modal-tipo-badge">
                                        {dadosUsuario.tipo === 'motorista' ? '🚚 Transportador' : '🧑 Usuário Agroov'}
                                    </span>
                                </div>
                                <div className="modal-info-list">
                                    <div className="modal-info-item">
                                        <span className="info-label">📧 E-mail:</span>
                                        <span className="info-value">{dadosUsuario.email || 'Não informado'}</span>
                                    </div>
                                    <div className="modal-info-item">
                                        <span className="info-label">📞 Telefone:</span>
                                        <span className="info-value">{dadosUsuario.telefone || 'Não informado'}</span>
                                    </div>
                                    {dadosUsuario.telefone && (
                                        <a 
                                            href={`https://wa.me/55${dadosUsuario.telefone.replace(/\D/g, '')}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn-whatsapp"
                                        >
                                            Chamar no WhatsApp 📱
                                        </a>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="modal-error">Não foi possível carregar os dados.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ChatWindow
